import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { LogSinkStatus } from '../lib/log-sink';
import { day } from '../lib/time';
import { Panel } from './Panel';
import { Button, buttonClass } from './ui/Button';

export type LogSinkCardProps = {
  readonly status: LogSinkStatus | undefined;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSave: (url: string) => void;
  readonly onClear: () => void;
};

/**
 * BYO log-sink panel (#448) — presenter only; the container (`AccountKeys`)
 * owns fetching + mutations. The URL is write-only: once set, only the
 * non-secret coords come back, rendered as a masked connection string (mirrors
 * the BYOK cards — sealed material exposes nothing to display). The form is the
 * last template child (happy-dom #481: nothing may follow a <form>).
 */
export function LogSinkCard(props: LogSinkCardProps): JSX.Element {
  const [url, setUrl] = createSignal('');

  const masked = (): string => {
    const s = props.status;
    if (!s?.configured) return '';
    return `postgres://•••@${s.host}:${s.port}/${s.database}`;
  };

  const submit = (e: Event): void => {
    e.preventDefault();
    const value = url().trim();
    if (value === '') return;
    props.onSave(value);
    setUrl('');
  };

  return (
    <Panel title={t('log_sink.title')} footer={t('log_sink.retention_note')} class="log-sink">
      <p class="card__desc keys-panel__desc">{t('log_sink.subtitle')}</p>

      <Show when={props.status?.configured ? props.status : undefined}>
        {(s) => (
          <div class="log-sink__current">
            <code class="log-sink__masked">{masked()}</code>
            <span class="log-sink__updated">
              {t('log_sink.updated', {
                when: day(s().updated_at_iso),
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={props.busy}
              onClick={() => props.onClear()}
            >
              {t('log_sink.clear')}
            </Button>
          </div>
        )}
      </Show>

      <form class="keys-form" onSubmit={submit}>
        <div class="keys-form__row">
          <input
            class="input"
            type="password"
            autocomplete="off"
            aria-label={
              props.status?.configured ? t('log_sink.url_replace') : t('log_sink.url_label')
            }
            placeholder={t('log_sink.url_placeholder')}
            value={url()}
            onInput={(e) => setUrl(e.currentTarget.value)}
          />
          <button type="submit" class={buttonClass()} disabled={props.busy || url().trim() === ''}>
            {props.busy ? t('log_sink.saving') : t('log_sink.save')}
          </button>
        </div>
        <p class="field__desc">{t('log_sink.pooler_hint')}</p>
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
      </form>
    </Panel>
  );
}
